<template>
  <PageChrome :show-header="false" tight>
    <div v-if="error" class="runError">{{ error }}</div>
    <div v-else-if="loading" class="runLoading">Loading...</div>

    <div v-else-if="run" class="runPage">
      <!-- Header card — account, status, run kind, "why it ran" -->
      <div class="headerCard">
        <div class="headerTop">
          <div class="headerLeft">
            <div class="headerLeftMain">
              <HeaderWithIcon :domain="run.account_domain || ''" variant="stacked">
                <template #primary>
                  <router-link v-if="run.account_id" :to="`/accounts/${run.account_id}`" class="accountLink">{{ accountName }}</router-link>
                  <span v-else class="accountName">{{ accountName }}</span>
                </template>
                <template #secondary>
                  <span>{{ formatTs(run.started_at || run.created_at) }}</span>
                  <span v-if="durationLabel">&middot; {{ durationLabel }}</span>
                </template>
              </HeaderWithIcon>
              <div class="statusRow">
                <AgentRunStatusPill :status="run.status" />
                <span v-if="run.test_mode" class="badge badgeTest">Test</span>
                <button
                  v-if="canRerun"
                  type="button"
                  class="rerunBtn"
                  :disabled="rerunning"
                  @click="rerunRun"
                >
                  <HeroIcon name="ArrowPathIcon" :size="13" />
                  {{ rerunning ? 'Re-running…' : 'Re-run' }}
                </button>
              </div>
              <div v-if="rerunError" class="rerunError">{{ rerunError }}</div>
            </div>
          </div>

          <div class="runKindInfo">
            <div class="runKindRow">
              <HeroIcon
                v-if="run.agent_icon_name"
                :name="run.agent_icon_name"
                :iconStyle="run.agent_icon_style"
                :color="run.agent_icon_color"
                :size="18"
              />
              <router-link v-if="run.agent_id" :to="`/agents/${run.agent_id}`" class="agentNameLink">
                {{ run.agent_name }}
                <span v-if="Number.isFinite(Number(run.agent_version))" class="agentVersion">v{{ Number(run.agent_version) }}</span>
              </router-link>
              <span v-else class="agentNameText">{{ run.agent_name }}</span>
            </div>
            <router-link v-if="run.sales_play_id" :to="`/sales-plays/${run.sales_play_id}`" class="salesPlayLink">
              {{ run.sales_play_name }}
            </router-link>
            <div v-if="run.run_source || run.triggered_by_user_name" class="reasonRow">
              <span class="reasonMuted">Why it ran:</span>
              <span class="sourcePill">{{ run.run_source || 'manual' }}</span>
              <template v-if="run.triggered_by_user_name">
                <span class="reasonSep">&middot;</span>
                <span class="triggerName">{{ run.triggered_by_user_name }}</span>
              </template>
            </div>
          </div>
        </div>

        <div class="summarySection" v-if="run.status_summary">
          <div class="summaryText">{{ run.status_summary }}</div>
        </div>

        <RunAccount v-if="run.account || run.receipt" :account="run.account" :receipt="run.receipt" />

        <div v-if="(run.reachout_pills || []).length > 0 || (run.staged_candidates || []).length > 0" class="pillsSection">
          <ReachoutPill v-for="rch in run.reachout_pills" :key="rch.id" :reachout="rch" />
          <router-link
            v-for="cand in run.staged_candidates"
            :key="cand.membership_id"
            :to="cand.kind === 'account' ? `/accounts/${cand.account_id}` : `/contacts/${cand.contact_id}`"
            class="n-pill stagedPill"
          >
            <AppIcon :name="cand.kind === 'account' ? 'account' : 'contact'" :size="12" />
            {{ cand.name || cand.domain || 'Candidate' }}
          </router-link>
        </div>
      </div>

      <!-- SSE activity feed — reused as-is from legacy. AgentRunActivity
           renders its own `.panel` chrome (global style) so we don't wrap
           it. `feedHeight` is the legacy max-height (px); passing a large
           sentinel uncaps it so the feed grows with content and the page
           scrolls. -->
      <AgentRunActivity
        class="activitySection"
        :runId="String(run.id)"
        title="Activity"
        subtitle="Streaming live events from the agent run."
        :showChat="false"
        :disabled="false"
        :showConnectionStatus="true"
        :feedHeight="99999"
        :agentName="run.agent_name"
        :agentIconName="run.agent_icon_name"
        :agentIconStyle="run.agent_icon_style"
        :agentIconColor="run.agent_icon_color"
        @status="onActivityStatus"
      />
    </div>

    <div v-else class="runEmpty">Run not found.</div>
  </PageChrome>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import PageChrome from '../components/PageChrome.vue'
import RunAccount from '../components/RunAccount.vue'
import HeaderWithIcon from '../../components/HeaderWithIcon.vue'
import HeroIcon from '../../components/HeroIcon.vue'
import AppIcon from '../../components/AppIcon.vue'
import AgentRunStatusPill from '../../components/AgentRunStatusPill.vue'
import ReachoutPill from '../components/ReachoutPill.vue'
import AgentRunActivity from '../../components/AgentRunActivity.vue'
import { useContextDrawer } from '../../drawer/useContextDrawer'
import { DRAWER_CONTEXT_ACCOUNT } from '../../drawer/drawerTypes'
import { apiFetch } from '../../services/api'
import { useChatScope } from '../composables/useChatScope'

const route = useRoute()
const router = useRouter()
const { setContext: setDrawerContext } = useContextDrawer()
const { setScope, clearScope } = useChatScope()

// ── State ────────────────────────────────────────────────────────

const loading = ref(false)
const error = ref('')
const run = ref<any>(null)
const rerunning = ref(false)
const rerunError = ref('')
let _runPollTimer: ReturnType<typeof setInterval> | null = null

// ── Computed ─────────────────────────────────────────────────────

const isInProgress = computed(() => {
  const st = String(run.value?.status || '').trim().toLowerCase()
  return st === 'queued' || st === 'running'
})

// Only prospect/sequence runs can be re-run — revision runs carry extra
// state (ReachoutRevision rows) a re-run doesn't reconstruct, and other
// modes aren't agent runs or have their own retry paths.
const _RERUNNABLE_MODES = new Set(['prospect', 'sequence'])
const canRerun = computed(() => {
  const mode = String(run.value?.execution_mode || '')
  return _RERUNNABLE_MODES.has(mode) && !isInProgress.value
})

const accountName = computed(() => {
  const name = String(run.value?.account_name || '').trim()
  if (name) return name
  const domain = run.value?.account_domain
  return domain || ''
})

const durationLabel = computed(() => {
  const start = run.value?.started_at || run.value?.created_at
  const end = run.value?.finished_at
  if (!start || !end) return ''
  try {
    const ms = new Date(end).getTime() - new Date(start).getTime()
    if (!Number.isFinite(ms) || ms < 0) return ''
    const totalSec = Math.floor(ms / 1000)
    const h = Math.floor(totalSec / 3600)
    const m = Math.floor((totalSec % 3600) / 60)
    const s = totalSec % 60
    const parts: string[] = []
    if (h > 0) parts.push(`${h}h`)
    if (m > 0) parts.push(`${m}m`)
    parts.push(`${s}s`)
    return parts.join(' ')
  } catch {
    return ''
  }
})

// ── Helpers ──────────────────────────────────────────────────────

function formatTs(ts: string | null | undefined): string {
  try {
    return new Date(ts!).toLocaleString()
  } catch {
    return String(ts || '')
  }
}

// ── Drawer context ───────────────────────────────────────────────

watch(() => run.value?.account_id, (id: string | undefined) => {
  setDrawerContext(id ? { type: DRAWER_CONTEXT_ACCOUNT, accountId: String(id) } : null)
})

// ── Chat scope ───────────────────────────────────────────────────

watch(() => run.value?.account_id, (accountId: string | undefined) => {
  const runId = String(route.params.id || '')
  if (accountId) {
    setScope({
      scopeType: 'account',
      scopeSubjectId: String(accountId),
      pageContext: { page_type: 'agent_run', agent_run_id: runId },
    })
  } else {
    setScope({
      scopeType: 'workspace',
      scopeSubjectId: '',
      pageContext: { page_type: 'agent_run', agent_run_id: runId },
    })
  }
})

// ── Data fetching ────────────────────────────────────────────────

async function refresh() {
  loading.value = true
  error.value = ''
  try {
    const id = String(route.params.id || '').trim()
    const runResp = await apiFetch(`/agent_runs/${encodeURIComponent(id)}`)
    run.value = runResp || null
  } catch (e: any) {
    error.value = e?.message || 'Failed'
    run.value = null
  } finally {
    loading.value = false
  }
}

function onActivityStatus(st: any) {
  if (st?.runStatus && run.value) {
    run.value = { ...run.value, status: st.runStatus }
  }
}

async function rerunRun() {
  const id = String(route.params.id || '').trim()
  if (!id || rerunning.value) return
  rerunning.value = true
  rerunError.value = ''
  try {
    const resp = await apiFetch(`/agent_runs/${encodeURIComponent(id)}/rerun`, { method: 'POST' })
    const newRunId = String(resp?.run_id || '').trim()
    if (newRunId) {
      router.push(`/agent-runs/${encodeURIComponent(newRunId)}`)
    }
  } catch (e: any) {
    rerunError.value = e?.message || 'Failed to re-run'
  } finally {
    rerunning.value = false
  }
}

// ── Polling ──────────────────────────────────────────────────────

function _startRunPolling() {
  _stopRunPolling()
  _runPollTimer = setInterval(async () => {
    if (!isInProgress.value) {
      _stopRunPolling()
      return
    }
    try {
      const id = String(route.params.id || '').trim()
      const resp = await apiFetch(`/agent_runs/${encodeURIComponent(id)}`)
      if (resp) run.value = resp
    } catch {
      // silent
    }
  }, 3000)
}

function _stopRunPolling() {
  if (_runPollTimer) {
    clearInterval(_runPollTimer)
    _runPollTimer = null
  }
}

// ── Lifecycle ────────────────────────────────────────────────────

watch(() => route.params.id, async () => {
  _stopRunPolling()
  await refresh()
  if (isInProgress.value) _startRunPolling()
})

onMounted(async () => {
  await refresh()
  if (isInProgress.value) _startRunPolling()
})

onBeforeUnmount(() => {
  _stopRunPolling()
  setDrawerContext(null)
  clearScope()
})
</script>

<style scoped>
/* ── Legacy CSS variable bridge for AgentRunActivity ─────────── */
.runPage {
  --border: var(--color-border-subtle);
  --surface: var(--color-bg-surface);
  --text: var(--color-text-primary);
  --muted: var(--color-text-tertiary);
  --primary: var(--color-accent);
  --success: var(--color-success);
  --danger: var(--color-danger);
  --badge-bg: var(--color-bg-subtle);
  --row-hover-bg: var(--color-bg-subtle);
  --field-bg: var(--color-bg-subtle);
  --table-header-bg: var(--color-bg-subtle);
  --link: var(--color-accent);
  --panel: var(--color-bg-surface);
  --focus-border: var(--color-accent);

  font-family: var(--font-sans);
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
}

.runError {
  color: var(--color-danger);
  font-family: var(--font-sans);
  font-size: var(--fs-sm);
}
.runLoading {
  color: var(--color-text-tertiary);
  font-family: var(--font-sans);
  font-size: var(--fs-sm);
}
.runEmpty {
  color: var(--color-text-tertiary);
  font-family: var(--font-sans);
  font-size: var(--fs-sm);
}

/* ── Header card ──────────────────────────────────────────────── */
.headerCard {
  flex-shrink: 0;
  background: var(--color-bg-surface);
  border: 1px solid var(--color-border-subtle);
  border-radius: var(--radius-lg);
  padding: var(--space-5);
  box-shadow: var(--shadow-sm);
}

.headerTop {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: var(--space-4);
}
.headerLeft {
  display: flex;
  align-items: flex-start;
  gap: var(--space-3);
}
.headerLeftMain {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  min-width: 0;
}
.statusRow {
  display: flex;
  align-items: center;
  gap: var(--space-2);
}

.accountLink {
  color: var(--color-text-primary);
  text-decoration: none;
}
.accountLink:hover { text-decoration: underline; }
.accountName {
  color: var(--color-text-primary);
}

/* ── Status badges ────────────────────────────────────────────── */
.badge {
  display: inline-flex;
  align-items: center;
  padding: 2px var(--space-2);
  border-radius: var(--radius-full);
  font-family: var(--font-sans);
  font-size: var(--fs-xs);
  font-weight: var(--fw-semibold);
  letter-spacing: var(--tracking-wide);
  text-transform: uppercase;
  border: 1px solid var(--color-border-subtle);
  background: var(--color-bg-subtle);
  color: var(--color-text-secondary);
}
.badgeTest {
  background: var(--hue-blue-soft);
  border-color: var(--hue-blue-edge);
  color: var(--hue-blue);
}

.rerunBtn {
  display: inline-flex;
  align-items: center;
  gap: var(--space-1);
  margin-left: var(--space-2);
  padding: 2px var(--space-2);
  border-radius: var(--radius-full);
  border: 1px solid var(--color-border-subtle);
  background: var(--color-bg-subtle);
  color: var(--color-text-secondary);
  font-family: var(--font-sans);
  font-size: var(--fs-xs);
  font-weight: var(--fw-semibold);
  cursor: pointer;
  transition: background var(--duration-fast) var(--ease-out);
}
.rerunBtn:hover:not(:disabled) {
  background: var(--color-bg-muted, var(--color-bg-subtle));
}
.rerunBtn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.rerunError {
  font-family: var(--font-sans);
  font-size: var(--fs-xs);
  color: var(--color-danger, #dc2626);
}

/* ── Run kind (right side): agent/block name, sales play, why it ran ── */
.runKindInfo {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: var(--space-1);
  min-width: 0;
  text-align: right;
}
.runKindRow {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  min-width: 0;
}
.agentNameLink {
  font-family: var(--font-sans);
  font-weight: var(--fw-semibold);
  font-size: var(--fs-sm);
  color: var(--color-text-primary);
  text-decoration: none;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.agentNameLink:hover { text-decoration: underline; }
.agentNameText {
  font-family: var(--font-sans);
  font-weight: var(--fw-semibold);
  font-size: var(--fs-sm);
  color: var(--color-text-primary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.agentVersion {
  color: var(--color-text-tertiary);
  font-size: var(--fs-xs);
  font-weight: var(--fw-regular);
}
.salesPlayLink {
  font-size: var(--fs-xs);
  color: var(--color-text-secondary);
  text-decoration: none;
}
.salesPlayLink:hover { text-decoration: underline; }

.reasonRow {
  display: flex;
  gap: var(--space-2);
  align-items: center;
  flex-wrap: wrap;
  justify-content: flex-end;
}
.sourcePill {
  display: inline-flex;
  align-items: center;
  padding: 2px var(--space-2);
  border-radius: var(--radius-full);
  border: 1px solid var(--color-border-subtle);
  font-size: var(--fs-xs);
  background: var(--color-bg-subtle);
  color: var(--color-text-secondary);
}
.reasonSep { color: var(--color-text-tertiary); }
.reasonMuted { color: var(--color-text-tertiary); font-size: var(--fs-xs); }
.triggerName {
  font-weight: var(--fw-semibold);
  font-size: var(--fs-xs);
}

/* ── Status summary ───────────────────────────────────────────── */
.summarySection {
  margin-top: var(--space-3);
  padding-top: var(--space-3);
  border-top: 1px solid var(--color-border-subtle);
}
.summaryText {
  font-size: var(--fs-sm);
  color: var(--color-text-primary);
  line-height: var(--lh-normal);
}

/* ── Reachout / staged-candidate pills ───────────────────────────── */
.pillsSection {
  margin-top: var(--space-3);
  padding-top: var(--space-3);
  border-top: 1px solid var(--color-border-subtle);
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-1);
}
.stagedPill {
  color: var(--color-text-secondary);
}

/* ── Activity feed section ────────────────────────────────────── */
/* AgentRunActivity is legacy and shared — it renders its own `.panel`
   chrome (global style). We set min-height on the component root so the
   region is comfortably tall even when only a few events have streamed
   in; passing a huge `feedHeight` uncaps the internal max-height so the
   feed grows with content and the page scrolls. */
.activitySection {
  min-height: 60vh;
}
</style>
